import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaPaperPlane, FaRobot, FaUser, FaTrash, FaTimes, FaExpand, FaCompress } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FileDownload from './FileDownload';
import { ITEMS, INVENTORY, ORDERS, VENDORS } from '../data/erpData';
import { useLang } from '../i18n/LangContext';
import '../styles/CopilotPanel.css';

// ═══════════════════════════════════════════════
// 데이터 기반 스마트 응답 엔진
// ═══════════════════════════════════════════════
const fmt = (n) => n != null ? Number(n).toLocaleString() : '-';

function buildLocalResponse(q, lang = 'ko', fmtCurrency) {
  const ql = q.toLowerCase();
  const isJa = lang === 'ja';
  const cur = fmtCurrency || ((v) => `₩${fmt(v)}`);

  // 특정 품목코드 재고 조회
  const codeMatch = q.match(/[Pp]\d{6}/);
  if (codeMatch) {
    const code = codeMatch[0].toUpperCase();
    const inv = INVENTORY.find(i => i.code === code);
    const item = ITEMS.find(i => i.code === code);
    const vs = VENDORS.filter(v => v.code === code);
    if (inv && item) {
      if (isJa) {
        let resp = `## 📦 ${code} (${item.name})\n\n`;
        resp += `| 項目 | 値 |\n|---|---|\n`;
        resp += `| 品目グループ | ${item.group} |\n`;
        resp += `| 品目分類 | ${item.cls} |\n`;
        resp += `| 仕入原価 | ${cur(item.cost)} |\n`;
        if (item.mfg > 0) resp += `| 製造原価 | ${cur(item.mfg)} |\n`;
        resp += `| Good 在庫 | ${fmt(inv.good)} EA |\n`;
        resp += `| Reserved | ${fmt(inv.reserved)} EA |\n`;
        resp += `| Defect | ${fmt(inv.defect)} EA |\n`;
        resp += `| **可用在庫(ATP)** | **${fmt(inv.atp)} EA** |\n`;
        resp += `| ステータス | ${inv.status} |\n`;
        if (inv.defect > 0) resp += `| 不良損失額 | ${cur(inv.lossAmt)} |\n`;
        if (vs.length > 0) {
          resp += `\n### 🤝 サプライヤー\n\n`;
          resp += `| サプライヤー | MOQ | 単価 | リードタイム | 不良率 |\n|---|---|---|---|---|\n`;
          vs.forEach(v => {
            resp += `| ${v.vName} | ${fmt(v.moq)} | ${cur(v.cost)} | ${v.lt}日 | ${(v.defRate * 100).toFixed(1)}% |\n`;
          });
        }
        if (inv.needOrder) resp += `\n> ⚠️ **発注が必要です。** ATP: ${fmt(inv.atp)}\n`;
        return resp;
      } else {
        let resp = `## 📦 ${code} (${item.name})\n\n`;
        resp += `| 항목 | 값 |\n|---|---|\n`;
        resp += `| 품목그룹 | ${item.group} |\n`;
        resp += `| 품목분류 | ${item.cls} |\n`;
        resp += `| 구매원가 | ${cur(item.cost)} |\n`;
        if (item.mfg > 0) resp += `| 제조원가 | ${cur(item.mfg)} |\n`;
        resp += `| Good 재고 | ${fmt(inv.good)} EA |\n`;
        resp += `| Reserved | ${fmt(inv.reserved)} EA |\n`;
        resp += `| Defect | ${fmt(inv.defect)} EA |\n`;
        resp += `| **가용재고(ATP)** | **${fmt(inv.atp)} EA** |\n`;
        resp += `| 상태 | ${inv.status} |\n`;
        if (inv.defect > 0) resp += `| 불량 손실액 | ${cur(inv.lossAmt)} |\n`;
        if (vs.length > 0) {
          resp += `\n### 🤝 공급업체\n\n`;
          resp += `| 공급업체 | MOQ | 단가 | 리드타임 | 불량율 |\n|---|---|---|---|---|\n`;
          vs.forEach(v => {
            resp += `| ${v.vName} | ${fmt(v.moq)} | ${cur(v.cost)} | ${v.lt}일 | ${(v.defRate * 100).toFixed(1)}% |\n`;
          });
        }
        if (inv.needOrder) resp += `\n> ⚠️ **발주가 필요합니다.** ATP: ${fmt(inv.atp)}\n`;
        return resp;
      }
    }
    return isJa ? `"${code}" の品目が見つかりません。` : `"${code}" 품목을 찾을 수 없습니다.`;
  }

  // 재고 현황 전체
  const isInventoryAll = isJa
    ? (ql.includes('在庫') || ql.includes('在庫状況') || ql.includes('在庫') || ql.includes('ざいこ')) && (ql.includes('全部') || ql.includes('全て') || ql.includes('状況') || ql.includes('教えて'))
    : ql.includes('재고') && (ql.includes('현황') || ql.includes('알려') || ql.includes('전체'));

  if (isInventoryAll) {
    if (isJa) {
      let resp = `## 📋 在庫状況一覧\n\n`;
      resp += `| 品目コード | 品目名 | Good | Reserved | ATP | ステータス |\n|---|---|---|---|---|---|\n`;
      INVENTORY.forEach(it => {
        const shortName = it.name.split(' ; ')[1] || it.name;
        const emoji = it.status === '발주필요' ? '🔴' : it.status === '주의' ? '🟡' : '🟢';
        const statusJa = it.status === '발주필요' ? '要発注' : it.status === '주의' ? '注意' : '正常';
        resp += `| ${it.code} | ${shortName} | ${fmt(it.good)} | ${fmt(it.reserved)} | **${fmt(it.atp)}** | ${emoji} ${statusJa} |\n`;
      });
      const need = INVENTORY.filter(i => i.needOrder);
      resp += `\n**総 ${INVENTORY.length}品目** 中 要発注: **${need.length}件**\n`;
      return resp;
    } else {
      let resp = `## 📋 전체 재고 현황\n\n`;
      resp += `| 품목코드 | 품목명 | Good | Reserved | ATP | 상태 |\n|---|---|---|---|---|---|\n`;
      INVENTORY.forEach(it => {
        const shortName = it.name.split(' ; ')[1] || it.name;
        const emoji = it.status === '발주필요' ? '🔴' : it.status === '주의' ? '🟡' : '🟢';
        resp += `| ${it.code} | ${shortName} | ${fmt(it.good)} | ${fmt(it.reserved)} | **${fmt(it.atp)}** | ${emoji} ${it.status} |\n`;
      });
      const need = INVENTORY.filter(i => i.needOrder);
      resp += `\n**총 ${INVENTORY.length}개** 중 발주필요: **${need.length}건**\n`;
      return resp;
    }
  }

  // 발주 필요
  const isNeedOrder = isJa
    ? (ql.includes('発注') || ql.includes('はっちゅう')) && (ql.includes('必要') || ql.includes('リスト') || ql.includes('教えて'))
    : ql.includes('발주') && (ql.includes('필요') || ql.includes('알려') || ql.includes('목록') || ql.includes('리스트'));

  if (isNeedOrder) {
    const need = INVENTORY.filter(i => i.needOrder);
    if (isJa) {
      let resp = `## ⚠️ 要発注品目 (${need.length}件)\n\n`;
      resp += `| # | 品目コード | 品目名 | Good | Reserved | ATP | 不足数 |\n|---|---|---|---|---|---|---|\n`;
      need.forEach((it, i) => {
        const shortName = it.name.split(' ; ')[1] || it.name;
        resp += `| ${i + 1} | ${it.code} | ${shortName} | ${fmt(it.good)} | ${fmt(it.reserved)} | **${fmt(it.atp)}** | ${fmt(Math.abs(it.atp))} |\n`;
      });
      return resp;
    } else {
      let resp = `## ⚠️ 발주 필요 품목 (${need.length}건)\n\n`;
      resp += `| # | 품목코드 | 품목명 | Good | Reserved | ATP | 부족량 |\n|---|---|---|---|---|---|---|\n`;
      need.forEach((it, i) => {
        const shortName = it.name.split(' ; ')[1] || it.name;
        resp += `| ${i + 1} | ${it.code} | ${shortName} | ${fmt(it.good)} | ${fmt(it.reserved)} | **${fmt(it.atp)}** | ${fmt(Math.abs(it.atp))} |\n`;
      });
      return resp;
    }
  }

  // 불량
  const isDefect = isJa
    ? (ql.includes('不良') || ql.includes('ふりょう'))
    : ql.includes('불량');

  if (isDefect) {
    const defs = INVENTORY.filter(i => i.defect > 0);
    const totalLoss = defs.reduce((s, i) => s + i.lossAmt, 0);
    if (isJa) {
      let resp = `## 🔴 不良在庫状況\n\n`;
      resp += `| # | 品目コード | 品目名 | 不良数量 | 単価 | 損失額 |\n|---|---|---|---|---|---|\n`;
      defs.forEach((it, i) => {
        const shortName = it.name.split(' ; ')[1] || it.name;
        resp += `| ${i + 1} | ${it.code} | ${shortName} | ${fmt(it.defect)} | ${cur(it.cost)} | ${cur(it.lossAmt)} |\n`;
      });
      resp += `\n**総損失額: ${cur(totalLoss)}**\n`;
      return resp;
    } else {
      let resp = `## 🔴 불량 재고 현황\n\n`;
      resp += `| # | 품목코드 | 품목명 | 불량수량 | 단가 | 손실액 |\n|---|---|---|---|---|---|\n`;
      defs.forEach((it, i) => {
        const shortName = it.name.split(' ; ')[1] || it.name;
        resp += `| ${i + 1} | ${it.code} | ${shortName} | ${fmt(it.defect)} | ${cur(it.cost)} | ${cur(it.lossAmt)} |\n`;
      });
      resp += `\n**총 손실액: ${cur(totalLoss)}**\n`;
      return resp;
    }
  }

  // 메모리 그룹
  const isMemory = isJa
    ? ql.includes('メモリ') && !ql.includes('非メモリ') && (ql.includes('グループ') || ql.includes('品目') || ql.includes('見せて'))
    : ql.includes('메모리') && !ql.includes('비메모리') && (ql.includes('그룹') || ql.includes('아이템') || ql.includes('보여') || ql.includes('품목'));

  if (isMemory) {
    const memItems = ITEMS.filter(i => i.group === '메모리');
    if (isJa) {
      let resp = `## 💾 メモリグループ (${memItems.length}品目)\n\n`;
      resp += `| 品目コード | 品目名 | 分類 | 仕入原価 |\n|---|---|---|---|\n`;
      memItems.forEach(it => { resp += `| ${it.code} | ${it.name} | ${it.cls} | ${cur(it.cost)} |\n`; });
      return resp;
    } else {
      let resp = `## 💾 메모리 그룹 (${memItems.length}개)\n\n`;
      resp += `| 품목코드 | 품목명 | 분류 | 구매원가 |\n|---|---|---|---|\n`;
      memItems.forEach(it => { resp += `| ${it.code} | ${it.name} | ${it.cls} | ${cur(it.cost)} |\n`; });
      return resp;
    }
  }

  // 비메모리 그룹
  const isNonMemory = isJa ? ql.includes('非メモリ') : ql.includes('비메모리');
  if (isNonMemory) {
    const items = ITEMS.filter(i => i.group === '비메모리');
    if (isJa) {
      let resp = `## 🔧 非メモリグループ (${items.length}品目)\n\n`;
      resp += `| 品目コード | 品目名 | 分類 | 仕入原価 |\n|---|---|---|---|\n`;
      items.forEach(it => { resp += `| ${it.code} | ${it.name} | ${it.cls} | ${cur(it.cost)} |\n`; });
      return resp;
    } else {
      let resp = `## 🔧 비메모리 그룹 (${items.length}개)\n\n`;
      resp += `| 품목코드 | 품목명 | 분류 | 구매원가 |\n|---|---|---|---|\n`;
      items.forEach(it => { resp += `| ${it.code} | ${it.name} | ${it.cls} | ${cur(it.cost)} |\n`; });
      return resp;
    }
  }

  // CSP
  if (ql.includes('csp') || ql.includes('CSP')) {
    const cspItems = ITEMS.filter(i => i.cls === 'CSP' || i.name.toLowerCase().includes('csp'));
    const cspVendors = VENDORS.filter(v => v.cls === 'CSP' || v.name.toLowerCase().includes('csp'));
    if (isJa) {
      let resp = `## 📦 CSP系列 (${cspItems.length}品目)\n\n`;
      resp += `| 品目コード | 品目名 | グループ | 仕入原価 |\n|---|---|---|---|\n`;
      cspItems.forEach(it => { resp += `| ${it.code} | ${it.name} | ${it.group} | ${cur(it.cost)} |\n`; });
      if (cspVendors.length > 0) {
        resp += `\n### サプライヤー別情報\n\n`;
        resp += `| 品目コード | サプライヤー | MOQ | 単価 | リードタイム | 不良率 |\n|---|---|---|---|---|---|\n`;
        cspVendors.forEach(v => { resp += `| ${v.code} | ${v.vName} | ${fmt(v.moq)} | ${cur(v.cost)} | ${v.lt}日 | ${(v.defRate * 100).toFixed(1)}% |\n`; });
      }
      return resp;
    } else {
      let resp = `## 📦 CSP 계열 (${cspItems.length}개)\n\n`;
      resp += `| 품목코드 | 품목명 | 그룹 | 구매원가 |\n|---|---|---|---|\n`;
      cspItems.forEach(it => { resp += `| ${it.code} | ${it.name} | ${it.group} | ${cur(it.cost)} |\n`; });
      if (cspVendors.length > 0) {
        resp += `\n### 업체별 정보\n\n`;
        resp += `| 품목코드 | 공급업체 | MOQ | 단가 | 리드타임 | 불량율 |\n|---|---|---|---|---|---|\n`;
        cspVendors.forEach(v => { resp += `| ${v.code} | ${v.vName} | ${fmt(v.moq)} | ${cur(v.cost)} | ${v.lt}일 | ${(v.defRate * 100).toFixed(1)}% |\n`; });
      }
      return resp;
    }
  }

  // 주문/구매요청
  const isOrder = isJa
    ? (ql.includes('注文') || ql.includes('購買依頼') || ql.includes('発注依頼'))
    : (ql.includes('주문') || ql.includes('구매 요청') || ql.includes('pr') || ql.includes('PR') || ql.includes('발주 요청'));

  if (isOrder) {
    if (isJa) {
      let resp = `## 📝 購買依頼書(PR)\n\n`;
      ORDERS.forEach(o => {
        const inv = INVENTORY.find(i => i.code === o.itemCode);
        const atp = inv ? inv.atp : 0;
        const shortage = o.qty - atp;
        resp += `---\n### ${o.id} | ${o.customer} | ${o.itemCode}\n`;
        resp += `- 注文: ${fmt(o.qty)} | ATP: ${fmt(atp)} | ルール: **${o.rule}**\n`;
        if (shortage > 0) {
          const vs = VENDORS.filter(v => v.code === o.itemCode);
          if (vs.length > 0) {
            const sorted = o.rule === 'Cost' ? [...vs].sort((a, b) => a.cost - b.cost) : [...vs].sort((a, b) => a.lt - b.lt);
            const best = sorted[0];
            let orderQty = Math.max(shortage, best.moq);
            orderQty = Math.ceil(orderQty / best.lot) * best.lot;
            resp += `- ⚠️ **不足: ${fmt(shortage)}個**\n`;
            resp += `- 推奨: **${best.vName}** | 発注: ${fmt(orderQty)} | ${cur(orderQty * best.cost)} | ${best.lt}日\n`;
          }
        } else {
          resp += `- ✅ **在庫充足**\n`;
        }
      });
      return resp;
    } else {
      let resp = `## 📝 구매 요청서(PR)\n\n`;
      ORDERS.forEach(o => {
        const inv = INVENTORY.find(i => i.code === o.itemCode);
        const atp = inv ? inv.atp : 0;
        const shortage = o.qty - atp;
        resp += `---\n### ${o.id} | ${o.customer} | ${o.itemCode}\n`;
        resp += `- 주문: ${fmt(o.qty)} | ATP: ${fmt(atp)} | 규칙: **${o.rule}**\n`;
        if (shortage > 0) {
          const vs = VENDORS.filter(v => v.code === o.itemCode);
          if (vs.length > 0) {
            const sorted = o.rule === 'Cost' ? [...vs].sort((a, b) => a.cost - b.cost) : [...vs].sort((a, b) => a.lt - b.lt);
            const best = sorted[0];
            let orderQty = Math.max(shortage, best.moq);
            orderQty = Math.ceil(orderQty / best.lot) * best.lot;
            resp += `- ⚠️ **부족: ${fmt(shortage)}개**\n`;
            resp += `- 추천: **${best.vName}** | 발주: ${fmt(orderQty)} | ${cur(orderQty * best.cost)} | ${best.lt}일\n`;
          }
        } else {
          resp += `- ✅ **재고 충족**\n`;
        }
      });
      return resp;
    }
  }

  // 공급업체
  const isVendor = isJa
    ? (ql.includes('サプライヤー') || ql.includes('供給') || ql.includes('業者'))
    : (ql.includes('공급') || ql.includes('벤더') || ql.includes('업체'));

  if (isVendor) {
    const vMap = {};
    VENDORS.forEach(v => { if (!vMap[v.vName]) vMap[v.vName] = []; vMap[v.vName].push(v); });
    if (isJa) {
      let resp = `## 🤝 サプライヤー状況\n\n`;
      Object.entries(vMap).forEach(([name, items]) => {
        const avgLt = (items.reduce((s, v) => s + v.lt, 0) / items.length).toFixed(1);
        const avgDef = ((items.reduce((s, v) => s + v.defRate, 0) / items.length) * 100).toFixed(1);
        resp += `### ${name} (${items.length}件) — 平均 ${avgLt}日 / ${avgDef}%\n\n`;
        resp += `| 品目コード | MOQ | 単価 | リードタイム | 不良率 |\n|---|---|---|---|---|\n`;
        items.forEach(v => { resp += `| ${v.code} | ${fmt(v.moq)} | ${cur(v.cost)} | ${v.lt}日 | ${(v.defRate * 100).toFixed(1)}% |\n`; });
        resp += '\n';
      });
      return resp;
    } else {
      let resp = `## 🤝 공급업체 현황\n\n`;
      Object.entries(vMap).forEach(([name, items]) => {
        const avgLt = (items.reduce((s, v) => s + v.lt, 0) / items.length).toFixed(1);
        const avgDef = ((items.reduce((s, v) => s + v.defRate, 0) / items.length) * 100).toFixed(1);
        resp += `### ${name} (${items.length}건) — 평균 ${avgLt}일 / ${avgDef}%\n\n`;
        resp += `| 품목코드 | MOQ | 단가 | 리드타임 | 불량율 |\n|---|---|---|---|---|\n`;
        items.forEach(v => { resp += `| ${v.code} | ${fmt(v.moq)} | ${cur(v.cost)} | ${v.lt}일 | ${(v.defRate * 100).toFixed(1)}% |\n`; });
        resp += '\n';
      });
      return resp;
    }
  }

  // 손실/폐기
  const isLoss = isJa
    ? (ql.includes('損失') || ql.includes('廃棄'))
    : (ql.includes('손실') || ql.includes('폐기'));

  if (isLoss) {
    const defs = INVENTORY.filter(i => i.defect > 0).sort((a, b) => b.lossAmt - a.lossAmt);
    const total = defs.reduce((s, i) => s + i.lossAmt, 0);
    if (isJa) {
      let resp = `## 📉 不良損失分析\n\n`;
      resp += `| 品目コード | 不良数量 | 単価 | 損失額 |\n|---|---|---|---|\n`;
      defs.forEach(it => { resp += `| ${it.code} | ${fmt(it.defect)} | ${cur(it.cost)} | ${cur(it.lossAmt)} |\n`; });
      resp += `\n**総損失額: ${cur(total)}**\n`;
      return resp;
    } else {
      let resp = `## 📉 불량 손실 분석\n\n`;
      resp += `| 품목코드 | 불량수량 | 단가 | 손실액 |\n|---|---|---|---|\n`;
      defs.forEach(it => { resp += `| ${it.code} | ${fmt(it.defect)} | ${cur(it.cost)} | ${cur(it.lossAmt)} |\n`; });
      resp += `\n**총 손실액: ${cur(total)}**\n`;
      return resp;
    }
  }

  // ATP / 가용재고
  const isAtp = isJa
    ? (ql.includes('可用在庫') || ql.includes('atp') || ql.includes('ATP'))
    : (ql.includes('가용') || ql.includes('atp') || ql.includes('ATP'));

  if (isAtp) {
    if (isJa) {
      let resp = `## 📊 可用在庫(ATP) 状況\n\n> ATP = Good - Reserved\n\n`;
      resp += `| 品目コード | 品目名 | Good | Reserved | ATP | ステータス |\n|---|---|---|---|---|---|\n`;
      [...INVENTORY].sort((a, b) => a.atp - b.atp).forEach(it => {
        const shortName = it.name.split(' ; ')[1] || it.name;
        const emoji = it.status === '발주필요' ? '🔴' : it.status === '주의' ? '🟡' : '🟢';
        resp += `| ${it.code} | ${shortName} | ${fmt(it.good)} | ${fmt(it.reserved)} | **${fmt(it.atp)}** | ${emoji} |\n`;
      });
      return resp;
    } else {
      let resp = `## 📊 가용재고(ATP) 현황\n\n> ATP = Good - Reserved\n\n`;
      resp += `| 품목코드 | 품목명 | Good | Reserved | ATP | 상태 |\n|---|---|---|---|---|---|\n`;
      [...INVENTORY].sort((a, b) => a.atp - b.atp).forEach(it => {
        const shortName = it.name.split(' ; ')[1] || it.name;
        const emoji = it.status === '발주필요' ? '🔴' : it.status === '주의' ? '🟡' : '🟢';
        resp += `| ${it.code} | ${shortName} | ${fmt(it.good)} | ${fmt(it.reserved)} | **${fmt(it.atp)}** | ${emoji} |\n`;
      });
      return resp;
    }
  }

  return null;
}

// ═══════════════════════════════════════════════
// CopilotPanel 컴포넌트
// ═══════════════════════════════════════════════
const CopilotPanel = ({ open, onToggle, currentPage }) => {
  const { lang, t, fmtCurrency } = useLang();
  const tc = t.copilot;

  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const FLOWISE_API_URL = 'https://www.windyflo.com/api/v1/prediction/e683b964-b8d2-441b-88af-265a0e9331b3';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const sendMessageToFlowise = async (message) => {
    try {
      const response = await fetch(FLOWISE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: message }),
      });
      if (!response.ok) throw new Error('API error');
      const data = await response.json();
      if (typeof data === 'string') return data;
      return data.text || data.answer || data.output || data.response || JSON.stringify(data);
    } catch (error) {
      console.error('Flowise API error:', error);
      return null;
    }
  };

  const handleSend = useCallback(async (text) => {
    const t_str = (typeof text === 'string' ? text : inputText).trim();
    if (!t_str || isLoading) return;

    setMessages(prev => [...prev, { id: Date.now(), type: 'user', text: t_str, timestamp: new Date() }]);
    setInputText('');
    setIsLoading(true);

    const localResp = buildLocalResponse(t_str, lang, fmtCurrency);
    if (localResp) {
      setTimeout(() => {
        setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: localResp, timestamp: new Date() }]);
        setIsLoading(false);
      }, 300 + Math.random() * 400);
    } else {
      const apiResp = await sendMessageToFlowise(t_str);
      setMessages(prev => [...prev, {
        id: Date.now() + 1, type: 'bot', timestamp: new Date(),
        text: apiResp || tc.notFound
      }]);
      setIsLoading(false);
    }
    inputRef.current?.focus();
  }, [inputText, isLoading, lang, fmtCurrency, tc]);

  const handleClear = () => {
    setMessages([]);
    setInputText('');
    setIsLoading(false);
  };

  const extractFileLinks = (text) => {
    const rx = /(https?:\/\/[^\s]+\.(?:xlsx|xls|docx|doc|pdf|csv|png|jpg|jpeg|gif)(?:[^\s]*))/gi;
    return (text.match(rx) || []).map(url => ({ url, filename: url.split('/').pop().split('?')[0] }));
  };

  const removeFileLinks = (text) => {
    return text.replace(/(https?:\/\/[^\s]+\.(?:xlsx|xls|docx|doc|pdf|csv|png|jpg|jpeg|gif)(?:[^\s]*))/gi, '').trim();
  };

  const prompts = (tc.prompts && tc.prompts[currentPage]) || (tc.prompts && tc.prompts.dashboard) || [];

  return (
    <>
      {/* 플로팅 토글 버튼 */}
      {!open && (
        <button className="copilot-fab" onClick={onToggle} title={tc.open}>
          <FaRobot />
          <span className="copilot-fab-badge">AI</span>
        </button>
      )}

      {/* 코파일럿 패널 */}
      <div className={`copilot-panel ${open ? 'open' : ''} ${expanded ? 'expanded' : ''}`}>
        {/* 헤더 */}
        <div className="copilot-header">
          <div className="copilot-header-left">
            <FaRobot className="copilot-header-icon" />
            <div>
              <div className="copilot-title">{tc.title}</div>
              <div className="copilot-subtitle">{ITEMS.length}{tc.subtitle}</div>
            </div>
          </div>
          <div className="copilot-header-actions">
            <button className="copilot-btn" onClick={handleClear} title={tc.clear} disabled={messages.length === 0}>
              <FaTrash />
            </button>
            <button className="copilot-btn" onClick={() => setExpanded(!expanded)} title={expanded ? tc.collapse : tc.expand}>
              {expanded ? <FaCompress /> : <FaExpand />}
            </button>
            <button className="copilot-btn copilot-close" onClick={onToggle} title={tc.close}>
              <FaTimes />
            </button>
          </div>
        </div>

        {/* 메시지 영역 */}
        <div className="copilot-messages">
          {messages.length === 0 && (
            <div className="copilot-welcome">
              <div className="copilot-welcome-icon">🤖</div>
              <p className="copilot-welcome-title">{tc.welcomeTitle}</p>
              <p className="copilot-welcome-sub">{tc.welcomeSub}</p>
              <div className="copilot-quick-prompts">
                {prompts.map((p, i) => (
                  <button key={i} className="copilot-prompt-btn" onClick={() => handleSend(p)}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`copilot-msg ${msg.type === 'user' ? 'copilot-msg-user' : 'copilot-msg-bot'}`}>
              <div className="copilot-msg-avatar">
                {msg.type === 'user' ? <FaUser /> : <FaRobot />}
              </div>
              <div className="copilot-msg-body">
                <div className="copilot-msg-text">
                  {msg.type === 'bot' ? (
                    <>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                        table: ({node, ...props}) => <div className="cp-table-wrap"><table className="cp-table" {...props} /></div>,
                        th: ({node, ...props}) => <th className="cp-th" {...props} />,
                        td: ({node, ...props}) => <td className="cp-td" {...props} />,
                      }}>
                        {removeFileLinks(msg.text)}
                      </ReactMarkdown>
                      {extractFileLinks(msg.text).map((f, idx) => (
                        <FileDownload key={idx} url={f.url} filename={f.filename} />
                      ))}
                    </>
                  ) : msg.text}
                </div>
                <div className="copilot-msg-time">
                  {msg.timestamp.toLocaleTimeString(lang === 'ja' ? 'ja-JP' : 'ko-KR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="copilot-msg copilot-msg-bot">
              <div className="copilot-msg-avatar"><FaRobot /></div>
              <div className="copilot-msg-body">
                <div className="copilot-typing"><span></span><span></span><span></span></div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 대화 중 퀵 프롬프트 */}
        {messages.length > 0 && (
          <div className="copilot-quick-bar">
            {prompts.slice(0, 3).map((p, i) => (
              <button key={i} className="copilot-quick-chip" onClick={() => handleSend(p)}>
                {p.length > 18 ? p.slice(0, 18) + '…' : p}
              </button>
            ))}
          </div>
        )}

        {/* 입력 */}
        <form className="copilot-input-area" onSubmit={(e) => { e.preventDefault(); handleSend(inputText); }}>
          <div className="copilot-input-row">
            <input
              ref={inputRef}
              type="text"
              className="copilot-input"
              placeholder={tc.placeholder}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={isLoading}
              maxLength={500}
            />
            <button type="submit" className="copilot-send" disabled={!inputText.trim() || isLoading}>
              <FaPaperPlane />
            </button>
          </div>
          <div className="copilot-footer">Powered by WindyFlo</div>
        </form>
      </div>
    </>
  );
};

export default CopilotPanel;
